/**
 * Citation Intelligence Types
 *
 * These types define the classification system for analyzing and categorizing
 * AI citations by page type, content format, media type, and authority level.
 */

// Re-export enums from Prisma for convenience
export type {
	CitationPageType,
	CitationContentFormat,
	CitationMediaType,
	CitationAuthorityType,
	CitationClassificationMethod,
} from "@repo/database";

/**
 * Page type classification - What kind of page this is on the website
 */
export const PAGE_TYPES = [
	"homepage",
	"blog_article",
	"product_page",
	"service_page",
	"pricing_page",
	"about_page",
	"documentation",
	"resource_page",
	"landing_page",
	"other",
] as const;

/**
 * Content format classification - The editorial format of the content
 */
export const CONTENT_FORMATS = [
	"listicle",
	"how_to_guide",
	"review",
	"research_data",
	"case_study",
	"video",
	"podcast",
	"news",
	"reference",
	"standard",
] as const;

/**
 * Media type classification - Owned, earned, or shared media
 */
export const MEDIA_TYPES = [
	"owned",
	"earned_press",
	"earned_mention",
	"review_platform",
	"social_community",
	"reference",
	"video_platform",
	"third_party",
] as const;

/**
 * Authority type classification - The credibility/authority category of the source
 */
export const AUTHORITY_TYPES = [
	"academic",
	"government",
	"major_media",
	"trade_media",
	"analyst",
	"professional_org",
	"review_aggregator",
	"encyclopedia",
	"company",
] as const;

/**
 * Authority types that are never a PR pitch target.
 *
 * A .gov page, a university paper or a Wikipedia article can cite you, but no
 * outreach email moves them: they publish on their own editorial rules and do
 * not run guest posts, product round-ups or vendor pitches. Recommending them
 * as "outlets to pitch" wastes the reader's time, so they come out of the
 * earned-media opportunity pool, the PR-target selection surfaces and the
 * export/report gap recommendations alike.
 *
 * They stay in every citation LIST — this is about what we recommend pitching,
 * never about hiding who cites you.
 */
export const NON_PITCHABLE_AUTHORITY_TYPES = [
	"government",
	"academic",
	"encyclopedia",
] as const;

const NON_PITCHABLE_AUTHORITY_SET: ReadonlySet<string> = new Set(
	NON_PITCHABLE_AUTHORITY_TYPES,
);

/** One publisher-level authority type (e.g. `Publisher.authorityType`). */
export function isNonPitchableAuthority(
	type: string | null | undefined,
): boolean {
	return !!type && NON_PITCHABLE_AUTHORITY_SET.has(type);
}

/**
 * "Any match" over a source's authority types.
 *
 * Accepts either a plain list or the per-source COUNT record the citation
 * dashboard rolls up (`{ government: 3 }`); zero counts never match, so a facet
 * key left behind by a filter can't bench a publisher.
 */
export function hasNonPitchableAuthority(
	types: readonly string[] | Record<string, number> | null | undefined,
): boolean {
	if (!types) {
		return false;
	}
	if (Array.isArray(types)) {
		return types.some((type) => isNonPitchableAuthority(type));
	}
	return Object.entries(types as Record<string, number>).some(
		([type, count]) => count > 0 && isNonPitchableAuthority(type),
	);
}

/**
 * Classification methods
 */
export const CLASSIFICATION_METHODS = ["heuristic", "llm"] as const;

/**
 * Input data for classifying a citation
 */
export interface CitationClassificationInput {
	url: string;
	title?: string | null;
	description?: string | null;
	/**
	 * Whether this citation is from the property's own domain.
	 * Used for backward compatibility and for tracking "our brand" vs "competitor" citations separately.
	 */
	isOurDomain: boolean;
	/**
	 * Optional list of normalized domains considered "owned" for mediaType classification.
	 * This should include both the property's domain AND competitor domains.
	 * When provided, mediaType will be set to "owned" if the citation domain matches ANY of these.
	 *
	 * Example: ["mybrand.com", "competitor1.com", "competitor2.io"]
	 *
	 * If not provided, falls back to using isOurDomain flag only.
	 */
	ownedDomains?: string[];
	brandDomain?: string; // The target brand's domain (deprecated, use ownedDomains)
	/** Dry runs: reuse stored labels, but never write a new one. */
	skipLabelStore?: boolean;
}

/**
 * Classification result for a single field
 */
export interface FieldClassification<T> {
	value: T;
	confidence: number;
}

/**
 * Full classification result for a citation
 */
export interface CitationClassificationResult {
	pageType: FieldClassification<(typeof PAGE_TYPES)[number]>;
	contentFormat: FieldClassification<(typeof CONTENT_FORMATS)[number]>;
	mediaType: FieldClassification<(typeof MEDIA_TYPES)[number]>;
	authorityType: FieldClassification<(typeof AUTHORITY_TYPES)[number]>;
	isListicle: boolean;
	urlDepth: number;
	classificationMethod: (typeof CLASSIFICATION_METHODS)[number];
	overallConfidence: number;
}

/**
 * Partial classification result (when only some fields could be determined)
 */
export interface PartialClassificationResult {
	pageType?: FieldClassification<(typeof PAGE_TYPES)[number]>;
	contentFormat?: FieldClassification<(typeof CONTENT_FORMATS)[number]>;
	mediaType?: FieldClassification<(typeof MEDIA_TYPES)[number]>;
	authorityType?: FieldClassification<(typeof AUTHORITY_TYPES)[number]>;
	isListicle?: boolean;
	urlDepth: number;
}

/**
 * Input for page-only classification (when Publisher provides domain-level fields)
 */
export interface PageClassificationInput {
	url: string;
	title?: string | null;
	description?: string | null;
	isOurDomain: boolean;
	ownedDomains?: string[];
	publisherAuthorityType: (typeof AUTHORITY_TYPES)[number];
	publisherMediaType: (typeof MEDIA_TYPES)[number];
}

/**
 * LLM classification request - only fields with low confidence
 */
export interface LlmClassificationRequest {
	url: string;
	title: string;
	description: string;
	fieldsNeeded: Array<
		"page_type" | "content_format" | "media_type" | "authority_type"
	>;
}

/**
 * LLM classification response
 */
export interface LlmClassificationResponse {
	page_type: (typeof PAGE_TYPES)[number] | null;
	content_format: (typeof CONTENT_FORMATS)[number] | null;
	media_type: (typeof MEDIA_TYPES)[number] | null;
	authority_type: (typeof AUTHORITY_TYPES)[number] | null;
	confidence: number;
}
